import React from "react";
import { ProjectOverviewDto } from "../../../types/projectTypes";
import { Paper, useTheme, Table, TableCell, TableHead, TableRow, TableContainer, TableBody } from "@mui/material";
import useResponsive from "../../../hooks/useResponsive";
import ProjectChip from "../projectCard/ProjectChip";
import { useNavigate } from "react-router-dom";
import ProjectTableHeadCell from "../ProjectTableHeadCell";
import { formatDate } from "../../../utils/dateUtils";

interface AllProjectsTableProps {
  projects: ProjectOverviewDto[];
  sortedBy: string;
  onChangeSortBy: (column: string) => void;
}

/**
 * AllProjectsTable component to display all projects in a table
 * @param param - Projects to be displayed in a table
 * @returns - A table with all projects
 */
const AllProjectsTable = ({ projects, onChangeSortBy, sortedBy }: AllProjectsTableProps) => {
  const theme = useTheme();
  const isMobile = useResponsive("down", "sm");
  const navigate = useNavigate();

  // styles for the table
  const styles = {
    tableHeader: {
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.primary.contrastText,
      textAlign: "center",
      whiteSpace: "nowrap",
      overflow: "hidden",
    },
    tableRow: {
      "&:nth-of-type(odd)": { backgroundColor: "#fff" },
      "&:nth-of-type(even)": { backgroundColor: "#ededed" },
      cursor: "pointer",
    },
    tableCell: {
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
      paddingRight: 0,
    },
  };

  // Handle row click to navigate to project details page
  const handleRowClick = (projectId: number) => {
    navigate(`/projekte/${projectId}`);
  };

  // Display a smaller table for mobile devices
  if (isMobile) {
    return (
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead sx={styles.tableHeader}>
            <TableRow>
              <ProjectTableHeadCell column="Projektname" sortedBy={sortedBy} onChangeSortBy={onChangeSortBy} />
              <ProjectTableHeadCell column="Status" sortedBy={sortedBy} onChangeSortBy={onChangeSortBy} />
            </TableRow>
          </TableHead>
          <TableBody>
            {projects.map((project) => (
              <TableRow key={project.projectId} sx={styles.tableRow} onClick={() => handleRowClick(project.projectId)}>
                <TableCell sx={[styles.tableCell, { maxWidth: 150 }]}>{project.projectName}</TableCell>
                <TableCell sx={[styles.tableCell, { maxWidth: 200 }]}>
                  <ProjectChip status={project.status} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  }

  // Display a larger table for desktop devices
  return (
    <TableContainer component={Paper}>
      <Table size="small">
        <TableHead sx={styles.tableHeader}>
          <TableRow>
            <ProjectTableHeadCell column="Projektname" sortedBy={sortedBy} onChangeSortBy={onChangeSortBy} />
            <ProjectTableHeadCell column="Branche" sortedBy={sortedBy} onChangeSortBy={onChangeSortBy} />
            <ProjectTableHeadCell column="Unternehmen" sortedBy={sortedBy} onChangeSortBy={onChangeSortBy} />
            <ProjectTableHeadCell column="Kernkompetenz" sortedBy={sortedBy} onChangeSortBy={onChangeSortBy} />
            <ProjectTableHeadCell column="Beginn" sortedBy={sortedBy} onChangeSortBy={onChangeSortBy} />
            <ProjectTableHeadCell column="Ende" sortedBy={sortedBy} onChangeSortBy={onChangeSortBy} />
            <ProjectTableHeadCell column="BT" sortedBy={sortedBy} onChangeSortBy={onChangeSortBy} />
            <ProjectTableHeadCell column="Status" sortedBy={sortedBy} onChangeSortBy={onChangeSortBy} />
          </TableRow>
        </TableHead>
        <TableBody>
          {projects.map((project) => (
            <TableRow key={project.projectId} sx={styles.tableRow} onClick={() => handleRowClick(project.projectId)}>
              <TableCell sx={[styles.tableCell, { maxWidth: 200 }]}>{project.projectName}</TableCell>
              <TableCell sx={[styles.tableCell, { maxWidth: 100 }]}>{project.client.industry.description}</TableCell>
              <TableCell sx={[styles.tableCell, { maxWidth: 100 }]}>{project.client.companyName}</TableCell>
              <TableCell sx={[styles.tableCell, { maxWidth: 100 }]}>
                {project.coreCompetencies.map((cc) => cc.designation).join(", ")}
              </TableCell>
              <TableCell sx={[styles.tableCell, { maxWidth: 100 }]}>
                {project.kickoff ? formatDate(project.kickoff) : formatDate(project.tenderDate)}
              </TableCell>
              <TableCell sx={[styles.tableCell, { maxWidth: 100 }]}>{formatDate(project.projectEnd)}</TableCell>
              <TableCell align="right" sx={[styles.tableCell, { maxWidth: 50 }]}>
                {project.soldBT
                  ? project.soldBT
                  : project.estimatedProjectBTmin === project.estimatedProjectBTmax
                  ? project.estimatedProjectBTmin
                  : `${project.estimatedProjectBTmin} - ${project.estimatedProjectBTmax}`}
              </TableCell>
              <TableCell sx={[styles.tableCell, { maxWidth: 200 }]}>
                <ProjectChip status={project.status} />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default AllProjectsTable;
